'use client';

import clsx from 'clsx';
import { useSiteStore } from '@/store/site';
import { useBetslipStore } from '@/store/betslip';
import { floatBetSlipButtonStyle } from './float-bet-slip-button.style';
import { Icon } from '@/components/icons/svg-icons';
import { Tooltip } from '@/components/widgets/tooltip';
import { tooltip } from 'styled-system/recipes';
import { BETTING_CODE, BETTING_CODE_MAP, CanBetResultModel, ParlayTicketModel, selectTicketMaxLangth, TicketModel, useTicketInfo } from '@/hooks/query';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useFloatBetslip } from '@/features/betslip/hook/use-float-betslip';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { useAlertBetslip } from '@/features/betslip/hook/use-alert-betslip';
import { useAccountInfo } from '@/hooks/query/account';
import { usePushAside } from '@/hooks/use-push-aside';
import { useSelectedLayoutSegments } from 'next/navigation';

const useMessage = (isParlayMode: boolean, canBetResult: CanBetResultModel, comboData: ParlayTicketModel[] | undefined, currentMultiTickets: TicketModel[]) => {
	const t = useTranslations('General');
	const [tooltipMessage, setTooltipMessage] = useState('');
	const getInvalidSelectionMessage = () => {
		// 相同賽事
		const set = new Set<number>(currentMultiTickets.map(ticket => ticket.Matchid));
		// match id 塞到set後, 如果有重複的直就無法塞入, 然後比對長度的結果就會是false
		const hasSameMatch = set.size !== currentMultiTickets.length;
		if (hasSameMatch) {
			const resourceKey = BETTING_CODE_MAP.get(BETTING_CODE.SameMatch)?.msgResourceKey;
			return t(resourceKey);
		}
	}

	const getMixParlayName = () => {
		// 串關成功
		const mixParlay = comboData?.find(parlay => parlay.isMixParlay && parlay.CanBet);
		if (mixParlay) {
			return `${mixParlay?.Name} @${mixParlay?.Odds}`;
		}
	}

	const getToastMessage = () => {
		// 上限20張
		const exceedLimitSelection = canBetResult.outerMsg.some(m => m.code === BETTING_CODE.ExceedLimitSelection);
		if (exceedLimitSelection) {
			const resourceKey = BETTING_CODE_MAP.get(BETTING_CODE.ExceedLimitSelection)?.msgResourceKey;
			document.dispatchEvent(new CustomEvent('show-toast-msg', {
				detail: { msg: t(resourceKey, { 0: selectTicketMaxLangth.toString() }) }
			}));
			return;
		}

		// Exceed Max Payout
		const exceedMaxPayout = canBetResult.outerMsg.find(m => ([BETTING_CODE.ParlayMaxBetLessThanMinBet, BETTING_CODE.ParlayNoComboAvailable] as Array<number>).includes(m.code));
		if (exceedMaxPayout) {
			document.dispatchEvent(new CustomEvent('show-toast-msg', {
				detail: { msg: exceedMaxPayout.msg }
			}));
			return;
		}
	}

	useEffect(() => {
		if (isParlayMode) {
			const message = getInvalidSelectionMessage();
			if (message) {
				setTooltipMessage(message);
			}
		}
	}, [isParlayMode, currentMultiTickets.length, canBetResult.innerMsg.length, canBetResult.outerMsg.length]);

	useEffect(() => {
		if (isParlayMode) {
			const message = getMixParlayName();
			if (message) {
				setTooltipMessage(message);
			}
		}
	}, [isParlayMode, comboData]);

	useEffect(() => {
		if (isParlayMode) {
			getToastMessage();
		}
	}, [canBetResult.innerMsg.length, canBetResult.outerMsg.length]);

	return { tooltipMessage, setTooltipMessage };
}



export const FloatBetSilpButton = () => {
	const selectedLayoutSegments = useSelectedLayoutSegments();
	const isParlayMode = useSiteStore(state => state.isParlayMode);
	const view = useAppearanceStore(state => state.view);
	const { user } = useAccountInfo();
	const { activeFloatBetSlip } = useFloatBetslip();
	const pushAside = usePushAside();
	const [
		betslipType,
		currentMultiTickets,
		currentParlayTicketModel,
		canBetResult,
		isExpand,
		toggleIsExpand
	] = useBetslipStore(
		state => [
			state.betslipType,
			state.currentMultiTickets,
			state.currentParlayTicketModel,
			state.canBetResult,
			state.isExpand,
			state.toggleIsExpand
		]
	);
	const { refetch } = useTicketInfo(currentMultiTickets);

	const { tooltipMessage, setTooltipMessage } = useMessage(
		isParlayMode || view === ViewEnum.galaxy, // galaxy 浮動模式無論如何都需跳提示, 因galaxy沒有parlay mode
		canBetResult,
		currentParlayTicketModel?.comboData,
		currentMultiTickets
	);

	const { showAlert } = useAlertBetslip(user?.BettingMode === 1);

	const clickParlayButton = () => {
		if (!isExpand) {
			refetch();
		}
		toggleIsExpand();
	};

	const hasTicket = currentMultiTickets.length > 0;
	
	// Hide button only when route is /promotion/* (but not on /promotion itself)
	const isPromotionSubPage = selectedLayoutSegments.length > 1 && selectedLayoutSegments[0] === 'promotion';
	
	return activeFloatBetSlip && betslipType != 'quickbet' && !isPromotionSubPage ? (
		<button
			className={clsx(
				'parlay-button',
				floatBetSlipButtonStyle.root,
				{ [floatBetSlipButtonStyle.pushAside]: pushAside }
			)}
			data-active={currentMultiTickets.length > 0 ? true : false}
			onClick={clickParlayButton}
		>
			<Icon name="betslip-bet" className={floatBetSlipButtonStyle.icon} />
			{showAlert ? (
				<div className={floatBetSlipButtonStyle.alertIcon}>
					<Icon name="ticket-exclamation" />
				</div>
			) : hasTicket ? (
				<div className={clsx('badge', floatBetSlipButtonStyle.badge)}>
					{currentMultiTickets.length}
				</div>
			) : null}
			<Tooltip
				message={tooltipMessage}
				className={tooltip({ visual: 'textSecondary', horizontal: 'right', vertical: 'top' })}
				countdownTime={3000}
				clearMessage={() => setTooltipMessage('')}
			/>
		</button>
	) : null
}
